"use client";

import React, { useMemo, useState, useEffect, memo, useCallback } from "react";
import Link from "@/components/Link";
import { useRouter, usePathname } from "next/navigation";
import { useNativeSignIn } from "@/hooks/useNativeSignIn";
import { usePriceMonitorRequest } from "@/hooks/usePriceMonitorRequest";
import {
  ActionIcon,
  Text,
  Tooltip,
  UnstyledButton,
  Anchor as MantineAnchor,
  Box as MantineBox,
  Image as MantineImage,
  Text as MantineText,
  Title as MantineTitle,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { sendGtagEvent } from "@/lib/client/gtag";
import { trackOutboundClick } from "@/lib/client/outboundClickNag";
import {
  trackItemOutboundClick,
  trackItemImageSearch,
  trackItemSlideinOpen,
} from "@/lib/client/itemTracking";
import { setLastHoveredItemId } from "@/lib/client/hoverTracking";
import useMostRecentViews from "@/hooks/useMostRecentViews";
import { useLazyLoadImage } from "@/hooks/useLazyLoadImage";
import { responsiveImageProps } from "@/lib/shared/optimizedImage";
import { useAuthContext } from "@/context/authContext";
import { useGlobalData } from "@/context/globalContext";
import { useFiltersStore } from "@/stores/filtersStore";
import { useCollectionsStore } from "@/stores/collectionsStore";
import { usePriceDropStore } from "@/stores/priceDropStore";
import { getItemTimestampDisplay } from "@/lib/client/utils";
import { formatPriceWithSymbol, getDisplayedPriceNumeric } from "@/lib/shared/currency";
import { formatSize } from "@/lib/shared/utils";
import { useImageSearchStore } from "@/stores/imageSearchStore";
import { useUiStore } from "@/stores/uiStore";
import { useAriaAnnouncerStore } from "@/stores/ariaAnnouncerStore";
import { usePriceInsights } from "@/hooks/usePriceInsights";
import PriceBadge from "@/components/PriceBadge";
import { IconPhotoSearch, IconBell, IconSettings } from "@tabler/icons-react";
import CouponIcon from "@/icons/Coupon";
import AuctionIcon from "@/icons/Auction";
import NewWindowIcon from "@/icons/NewWindow";
import WarningIcon from "@/icons/Warning";
import FavoriteButton from "./FavoriteButton";

import styles from "./styles.module.scss";
import interactionStyles from "./interaction.module.scss";

// Sentinel `url`/`itemurl` value for injected ad cards (see
// VirtualizedCardGrid's signup-variant promo card) that should open Clerk's
// sign-in modal instead of navigating anywhere — there's no dedicated
// sign-up page to route to, sign-in is normally triggered imperatively.
export const AD_ACTION_SIGN_IN = "__open-sign-in__";

// Helper function to get display size (prioritize ctsize, then size)
const getDisplaySize = (item: SearchResultItem): string | null => {
  if (item.ctsize) return item.ctsize;
  if (item.size) return item.size;
  return null;
};

type Props = {
  item: SearchResultItem;
  small?: boolean;
  /** Render the square image-and-tags treatment without the card footer */
  imageOnly?: boolean;
  mostRecent?: boolean;
  ignoreResponsive?: boolean;
  fixed?: boolean;
  onClickCallback?: (item: SearchResultItem) => void;
  priceHasCurrency?: boolean;
  onFavoriteUpdate?: () => void;
  hideFavoriteCount?: boolean;
  disableFavorites?: boolean;
  routeToProductPage?: boolean;
  enableImageSearch?: boolean;
  favoriteId?: number; // For price drop monitoring
  enablePriceMonitor?: boolean; // Enable price drop monitor button
  /** Force the share link icon visible even in small mode */
  showShareLink?: boolean;
  /** Override the scroll container selector for useLazyLoadImage (default: "#masonaryContainer") */
  containerSelector?: string;
};

const ItemCard = memo(
  ({
    item,
    small,
    imageOnly = false,
    ignoreResponsive = false,
    mostRecent = false,
    fixed = false,
    onClickCallback,
    onFavoriteUpdate,
    hideFavoriteCount = false,
    disableFavorites = false,
    routeToProductPage = false,
    enableImageSearch = false,
    favoriteId,
    enablePriceMonitor = false,
    showShareLink = false,
    containerSelector,
  }: Props) => {
    const { updateMostRecent } = useMostRecentViews();
    const router = useRouter();
    const pathname = usePathname();
    const { openSignIn } = useNativeSignIn();
    const { requestPriceMonitor } = usePriceMonitorRequest();

    const { isSignedIn, isAdmin } = useAuthContext();
    const { exchangeRates } = useGlobalData();
    const activeFilters = useFiltersStore((s) => s.activeFilters);
    const activeCollectionItemIds = useCollectionsStore((s) => s.activeCollectionItemIds);
    const { optimisticallyAddItem, optimisticallyRemoveItem } = useCollectionsStore(
      (s) => s.actions
    );
    const itemLikedByInfo = useCollectionsStore((s) => s.itemLikedByInfo.get(item.id) || null);
    const {
      setSlideInItem,
      setItemModalOpen,
      setFavoritesModalOpen,
      openAdminItemModal,
      openWarningModal,
    } = useUiStore((s) => s.actions);
    const favoritesModalOpen = useUiStore((s) => s.favoritesModalOpen);
    const { startSearch: startImageSearch } = useImageSearchStore((s) => s.actions);
    const { announce } = useAriaAnnouncerStore((s) => s.actions);

    const [isAnimating, setIsAnimating] = useState(false);

    const isMonitored = usePriceDropStore((state: { isMonitored: (id: number) => boolean }) =>
      state.isMonitored(item.id)
    );

    const isFavorited = activeCollectionItemIds.has(item.id);
    const isAdCard = item.id === 0;

    // Image URLs go through Netlify Image CDN so the grid serves
    // width-appropriate WebP/AVIF instead of full-size vendor originals.
    // Some vendor CDNs (e.g. hotlink protection) block the optimizer's
    // server-side fetch while allowing browsers, so failures step down:
    // optimized -> original vendor URL -> broken placeholder. The stage is
    // React state (not a DOM mutation) so re-renders can't resurrect a URL
    // that already failed.
    const [imgStage, setImgStage] = useState<"optimized" | "original" | "broken">("optimized");
    const rawSrc = item.imageurl || "";
    const rawLarge = item.largeImage ?? item.imageurl ?? "";
    const imageCandidates = [
      { src: rawSrc, width: 480 },
      { src: rawLarge, width: 1024 },
    ];
    const optimizedImage = responsiveImageProps(imageCandidates, { preferredWidth: 480 });
    const originalImage = responsiveImageProps(imageCandidates, {
      optimize: false,
      preferredWidth: 480,
    });

    useEffect(() => {
      setImgStage("optimized");
    }, [rawSrc]);

    const effectiveImage = imgStage === "optimized" ? optimizedImage : originalImage;

    // Lazy load image with 600px margin
    const { imgRef, loadedSrc, loadedSrcSet } = useLazyLoadImage({
      src: imgStage === "broken" ? "" : effectiveImage.src,
      srcSet: imgStage === "broken" ? undefined : effectiveImage.srcSet,
      rootMargin: "0px 0px 600px 0px",
      ...(containerSelector ? { containerSelector } : {}),
    });

    const priceText =
      item.rentable && item.priceusd === 0
        ? "FOR RENT"
        : formatPriceWithSymbol({
            price: item.price,
            currencyCode: item.currency,
            abbreviate: true,
            priceUsd: item.priceusd,
            roundToFive: false,
            rawPrice: item.rawPrice,
          });
    const priceClass = useMemo(() => {
      if (priceText.length > 11) return styles.long;
      return "";
    }, [priceText]);
    const favoriteCount = itemLikedByInfo?.total_users || item?.favorite_count || 0;
    const priceInsights = usePriceInsights(item);
    const displayedPriceForBadge =
      item.rentable && item.priceusd === 0
        ? 0
        : getDisplayedPriceNumeric({
            price: item.price,
            currencyCode: item.currency,
            abbreviate: true,
            priceUsd: item.priceusd,
            roundToFive: false,
            rawPrice: item.rawPrice,
          });
    const hasPriceDrop =
      !item.isSold &&
      !!item.old_priceusd &&
      !!item.new_priceusd &&
      !!displayedPriceForBadge &&
      !isNaN(displayedPriceForBadge) &&
      item.new_priceusd < item.old_priceusd &&
      (item.old_priceusd - item.new_priceusd) / item.old_priceusd >= 0.1;
    const timestampDisplay = getItemTimestampDisplay({
      item: item as SearchResultItem,
      forceAdded: true,
    });

    const handleClick = (e: React.SyntheticEvent<HTMLElement>) => {
      e.stopPropagation();
      e.preventDefault();

      // Signup-variant promo card — open the sign-in modal directly rather
      // than falling through to the url!==itemurl check below, since both
      // fields are intentionally the same sentinel here (there's no real
      // page for either of them to navigate to).
      if (item.url === AD_ACTION_SIGN_IN) {
        void openSignIn();
        return;
      }

      setSlideInItem(item);
      onClickCallback?.(item);

      // Check if this is a Pro ad or special card with a URL. Ad cards
      // (isAdCard) are checked directly rather than via url!==itemurl —
      // the "tokens" ad variant sets both to the same "/plans" (so the
      // vendor-label anchor at the bottom of the card, which links via
      // itemurl, still works as a plain link), which meant a full-card
      // click on that specific ad fell through to the normal item-click
      // path below and opened an empty slide-in for the fake id-0 item.
      if (item.url && (isAdCard || item.url !== item.itemurl)) {
        // If URL starts with /goto/, it's a vendor redirect - open slidein instead
        if (item.url.startsWith("/goto/")) {
          setItemModalOpen(true);
          return;
        }
        try {
          trackItemOutboundClick(item, "item_card_promo");
        } catch {
          // Analytics must never affect navigation
        }
        router.push(item.url);
        return;
      }

      if (!mostRecent) {
        updateMostRecent(item?.id);
      }

      sendGtagEvent(
        "itemcard_clicked",
        "user_interaction",
        `${item?.brand?.label} - ${item?.vendor?.label}`,
        {
          brand: `${item?.brand?.label}`,
          country: `${item?.country}`,
          vendor: `${item?.vendor?.label}`,
          price: `${item?.priceusd}`,
        }
      );

      if (routeToProductPage) {
        router.push(`/search/product/${item.id}`);
      } else {
        setSlideInItem(item);
        if (favoritesModalOpen) {
          setFavoritesModalOpen(false);
        }
        try {
          trackItemSlideinOpen(item);
        } catch {
          // Analytics must never affect UX
        }
        setItemModalOpen(true);
      }

      if (onClickCallback) {
        onClickCallback(item);
      }
    };

    const handleFavorite = async (e: React.SyntheticEvent<HTMLButtonElement>) => {
      e.stopPropagation();

      if (!isSignedIn || !item) {
        return;
      }

      setIsAnimating(true);
      setTimeout(() => setIsAnimating(false), 400);

      try {
        if (isFavorited) {
          await optimisticallyRemoveItem(item.id);
          announce(`Removed ${item.brand?.label || ""} ${item.title} from favorites`);
          sendGtagEvent(
            "favorite_removed",
            "user_interaction",
            `${item.brand?.label} - ${item?.vendor?.label}`,
            {
              brand: `${item.brand?.label}`,
              country: `${item.country}`,
              vendor: `${item?.vendor?.label}`,
              price: `${item.priceusd}`,
            }
          );
        } else {
          const result = await optimisticallyAddItem(item.id, item);
          if (!result.success) {
            notifications.show({
              title: "Couldn't add to favorites",
              message: result.error || "Please try again.",
              color: "red",
              position: "top-center",
              autoClose: 1500,
            });
            return;
          }
          announce(`Added ${item.brand?.label || ""} ${item.title} to favorites`);
          sendGtagEvent(
            "favorite_added",
            "user_interaction",
            `${item.brand?.label} - ${item?.vendor?.label}`,
            {
              brand: `${item.brand?.label}`,
              country: `${item.country}`,
              vendor: `${item?.vendor?.label}`,
              price: `${item.priceusd}`,
            }
          );
        }
        onFavoriteUpdate?.();
      } catch (error) {
        console.error("Error handling favorite:", error);
      }
    };

    const handleOutbound = useCallback(
      (event: React.SyntheticEvent<HTMLAnchorElement>) => {
        event.stopPropagation();
        if (!mostRecent) {
          updateMostRecent(item.id);
        }

        try {
          trackItemOutboundClick(item, "item_card", activeFilters);
        } catch {
          // Analytics must never affect outbound navigation
        }
        trackOutboundClick();
      },
      [item, mostRecent, updateMostRecent, activeFilters]
    );

    const handleImageSearch = (e: React.SyntheticEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      if (item.imageurl) {
        const onSearchPage = pathname === "/search";
        startImageSearch(
          item,
          onSearchPage ? activeFilters : null,
          undefined,
          exchangeRates as Record<string, number> | undefined
        );
        if (!onSearchPage) {
          router.push("/search?clearFilters=1");
        }
        trackItemImageSearch(item);
      }
    };

    const handleImageError = useCallback(() => {
      setImgStage((stage) => {
        // Only step down to "original" if the optimizer actually rewrote the
        // URL. When optimization is disabled, optimized === original, so a
        // failure goes straight to the broken placeholder.
        if (stage === "optimized" && optimizedImage.src !== rawSrc) return "original";
        return "broken";
      });
    }, [optimizedImage.src, rawSrc]);

    const handlePriceMonitorClick = useCallback(
      (e: React.SyntheticEvent<HTMLButtonElement>) => {
        e.stopPropagation();

        requestPriceMonitor(item, {
          favoriteId: favoriteId ?? null,
          source: "favorites",
        });
      },
      [favoriteId, item, requestPriceMonitor]
    );

    const handleAdminClick = useCallback(
      (e: React.SyntheticEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        openAdminItemModal(item);
      },
      [openAdminItemModal, item]
    );

    const handleVendorClick = useCallback(
      (e: React.SyntheticEvent<HTMLAnchorElement>) => {
        e.stopPropagation();
        if (item.itemurl === AD_ACTION_SIGN_IN) {
          e.preventDefault();
          void openSignIn();
          return;
        }
        handleOutbound(e);
      },
      [handleOutbound, item.itemurl, openSignIn]
    );

    const handleShareLinkClick = useCallback((e: React.SyntheticEvent<HTMLAnchorElement>) => {
      e.stopPropagation();
    }, []);

    const cardActionLabel = isAdCard
      ? item.title || "Open promotion"
      : `View ${item.brand?.label ? `${item.brand.label} ` : ""}${item.title}`.trim();

    return (
      <MantineBox
        component="article"
        className={`${styles.itemCard} ${interactionStyles.itemCard} ${isAdCard ? styles.proAdCard : ""}`}
        data-sold={item.isSold}
        onMouseEnter={() => setLastHoveredItemId(item.id)}
        data-small={small}
        data-image-only={imageOnly || undefined}
        data-fixed={fixed}
        data-force-vertical={ignoreResponsive}
        data-selected={isFavorited}
        data-blacklisted={item.blacklisted}
      >
        <UnstyledButton
          type="button"
          className={interactionStyles.cardPrimaryAction}
          onClick={handleClick}
          aria-label={cardActionLabel}
        />

        <MantineBox className={styles.imageContainer}>
          <MantineImage
            ref={imgRef}
            src={
              imgStage === "broken"
                ? "/kurb_broken_image.svg"
                : loadedSrc ||
                  "data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
            }
            alt={`${item.brand?.label || ""} ${item.title} - ${item.vendor?.label || ""}`.trim()}
            srcSet={loadedSrcSet}
            sizes="(max-width: 480px) 480px, 1024px"
            onError={handleImageError}
          />
          <MantineBox className={styles.mobileCarouselTags} aria-hidden="true">
            {item.brand?.label && (
              <MantineBox className={styles.mobileCarouselTag}>{item.brand.label}</MantineBox>
            )}
            <MantineBox className={styles.mobileCarouselTag}>{priceText}</MantineBox>
          </MantineBox>
          {item.rentable && <MantineBox className={styles.rentableBadge}>RENTABLE</MantineBox>}
          {item.imageurl && enableImageSearch && (
            <ActionIcon
              className={styles.imageSearchButton}
              onClick={handleImageSearch}
              aria-label="Find similar items"
              title="Find similar items"
              variant="transparent"
              size="md"
            >
              <IconPhotoSearch size={18} />
              <MantineText span className={styles.imageSearchText}>
                Find similar items
              </MantineText>
            </ActionIcon>
          )}
        </MantineBox>

        <MantineBox className={styles.content}>
          <MantineBox className={styles.header}>
            {item.brand?.label && (
              <MantineTitle order={3} className={styles.brand}>
                {item.brand.label}
              </MantineTitle>
            )}
            <MantineBox className={styles.headerRight}>
              {!item.isSold && !!item?.vendor?.coupons?.length && (
                <MantineBox className={styles.couponIcon} title="Coupons available">
                  <CouponIcon />
                </MantineBox>
              )}
              {item.saletype === 2 && (
                <MantineBox className={styles.auctionIcon} title="Auction item">
                  <AuctionIcon />
                </MantineBox>
              )}
              {item.blacklisted && (
                <Tooltip
                  label="Warning: This item has been blocklisted for potential authenticity or dropshipping concerns."
                  openDelay={200}
                  withArrow
                  multiline
                  maw={220}
                >
                  <UnstyledButton
                    className={styles.warningIcon}
                    aria-label="Why is this item flagged?"
                    onClick={(e) => {
                      e.stopPropagation();
                      openWarningModal();
                    }}
                    onKeyDown={(e) => {
                      e.stopPropagation();
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        openWarningModal();
                      }
                    }}
                  >
                    <WarningIcon />
                  </UnstyledButton>
                </Tooltip>
              )}
              {enablePriceMonitor && !!favoriteId && (
                <UnstyledButton
                  className={`${styles.headerFavorite} ${isMonitored ? styles.monitored : ""}`}
                  onMouseDown={(e) => {
                    e.stopPropagation();
                  }}
                  onPointerDown={(e) => {
                    e.stopPropagation();
                  }}
                  onClick={handlePriceMonitorClick}
                  aria-label={isMonitored ? "Price drop monitoring active" : "Monitor price drop"}
                  title={isMonitored ? "Monitoring for price drops" : "Monitor for price drops"}
                  data-monitored={isMonitored}
                >
                  <IconBell size={20} fill={isMonitored ? "currentColor" : "none"} />
                </UnstyledButton>
              )}
              {!isAdCard && (
                <FavoriteButton
                  isSignedIn={isSignedIn}
                  isFavorited={isFavorited}
                  disableFavorites={disableFavorites}
                  isAnimating={isAnimating}
                  handleFavorite={handleFavorite}
                  favoriteCount={favoriteCount}
                  hideFavoriteCount={hideFavoriteCount}
                />
              )}
              {isAdmin && !isAdCard && (
                <UnstyledButton
                  className={`${styles.headerFavorite} ${styles.adminButton}`}
                  onMouseDown={(e) => e.stopPropagation()}
                  onPointerDown={(e) => e.stopPropagation()}
                  onClick={handleAdminClick}
                  aria-label="Admin edit listing"
                  title="Admin edit listing"
                >
                  <IconSettings size={18} />
                </UnstyledButton>
              )}
            </MantineBox>
          </MantineBox>

          <MantineTitle order={4} className={styles.title}>
            {item.title}
          </MantineTitle>

          <MantineBox
            className={`${styles.locationPrice} ${!priceInsights.insight ? styles.locationPriceReversed : ""}`}
          >
            <MantineBox className={styles.leftSection}>
              <MantineBox className={styles.sizeVendorColumn}>
                {getDisplaySize(item) && (
                  <MantineBox className={styles.sizeDisplay}>
                    Size: {formatSize(getDisplaySize(item), item?.itemtype)}
                  </MantineBox>
                )}
                <MantineBox className={styles.vendorRow}>
                  <MantineAnchor
                    className={styles.vendor}
                    href={item.itemurl}
                    target="_blank"
                    rel="noreferrer"
                    onClick={handleVendorClick}
                    onKeyDown={(e) => e.stopPropagation()}
                  >
                    {item?.vendor?.label ?? ""}
                  </MantineAnchor>
                  {!!item.id && (
                    <Link
                      href={`/search/product/${item.id}`}
                      onClick={handleShareLinkClick}
                      onKeyDown={(e) => {
                        e.stopPropagation();
                      }}
                      className={`${styles.shareLink} ${showShareLink ? styles.shareLinkVisible : ""}`}
                      aria-label="View item"
                      target="_blank"
                      prefetch={false}
                    >
                      <NewWindowIcon />
                    </Link>
                  )}
                </MantineBox>
              </MantineBox>
            </MantineBox>
            <MantineBox className={styles.priceColumn}>
              {priceInsights.hasStats && priceInsights.insight ? (
                <Text
                  size="xs"
                  c={priceInsights.insight.color}
                  fw={priceInsights.insight.weight}
                  className={styles.priceInsight}
                  aria-label={priceInsights.insight.ariaLabel}
                >
                  {priceInsights.insight.label}
                </Text>
              ) : hasPriceDrop ? (
                // No insight but price dropped: badge takes the insight row (no spacer needed)
                <PriceBadge
                  priceChange={{
                    oldPriceUsd: item.old_priceusd,
                    newPriceUsd: item.new_priceusd,
                    changeTimestamp: item.change_timestamp,
                  }}
                  isSold={item.isSold}
                  className={styles.itemCardDropBadge}
                  displayedPrice={displayedPriceForBadge}
                />
              ) : !small ? (
                <MantineBox className={styles.priceInsightSpacer} />
              ) : null}
              <MantineBox className={`${styles.price} ${item.isSold ? styles.sold : ""}`}>
                {item.isSold && (
                  <MantineText span className={styles.soldLabel}>
                    Sold
                  </MantineText>
                )}
                <MantineBox className={styles.priceStack}>
                  {priceInsights.insight && (
                    // Insight exists: badge stays inside priceStack so both insight + badge show
                    <PriceBadge
                      priceChange={{
                        oldPriceUsd: item.old_priceusd,
                        newPriceUsd: item.new_priceusd,
                        changeTimestamp: item.change_timestamp,
                      }}
                      isSold={item.isSold}
                      className={styles.itemCardDropBadge}
                      displayedPrice={displayedPriceForBadge}
                    />
                  )}
                  <MantineBox className={`${styles.priceValue} ${priceClass}`}>
                    {priceText}
                  </MantineBox>
                </MantineBox>
              </MantineBox>
              {timestampDisplay && (
                <MantineText span className={styles.timeAgo}>
                  {timestampDisplay}
                </MantineText>
              )}
            </MantineBox>
          </MantineBox>
        </MantineBox>
      </MantineBox>
    );
  }
);

ItemCard.displayName = "ItemCard";

export default ItemCard;
